// Single source of truth for resolving team display attributes (name, abbr,
// primary_color, logo_url) across PWHL team_ids, NCAA school names, and
// user-created custom teams (ids prefixed "ct-").
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { TEAM_INFO } from "./teamMap";
import { ALL_NCAA_SCHOOLS } from "./ncaaData";
import { LEAGUES as LEAGUE_DEFS, LEAGUE_IDS } from "./leagues";

function ncaaOverrideKey(school) {
    const found = ALL_NCAA_SCHOOLS.find((s) => s.school === school);
    if (!found) return null;
    return `ncaa-${found.conference}-${school}`;
}

// team name -> { leagueId, color } for every non-PWHL/NCAA league team.
const LEAGUE_TEAM_INDEX = (() => {
    const out = {};
    LEAGUE_IDS.forEach((lid) => {
        const lg = LEAGUE_DEFS[lid] || {};
        const names = lg.conferences ? lg.conferences.flatMap((c) => c.teams || []) : (lg.teams || []);
        names.forEach((n) => { if (!out[n]) out[n] = { leagueId: lid, color: lg.color || "#1E2233" }; });
    });
    return out;
})();

function resolveName(originalName, override) {
    if (!override) return originalName;
    const city = (override.city_name || "").trim();
    const tname = (override.team_name || "").trim();
    if (city || tname) return `${city} ${tname}`.trim();
    if (override.custom_name) return override.custom_name;
    return originalName;
}

function buildResolvers(overridesMap, customMap) {
    function pwhl(teamId) {
        const base = TEAM_INFO[teamId];
        const ov = overridesMap[teamId];
        return {
            id: teamId,
            name: resolveName(base?.name || teamId, ov),
            abbr: ov?.abbreviation || base?.abbr || teamId.toUpperCase(),
            primary_color: ov?.primary_color || base?.primary_color || base?.color || "#1E2233",
            text_color: ov?.text_color || "#FFFFFF",
            logo_url: ov?.logo_url || base?.logo_url || "",
            standings_opacity: ov?.standings_opacity ?? 1,
            logo_opacity: ov?.logo_opacity ?? 1,
            logo_bg: ov?.logo_bg || "auto",
            logo_scale: ov?.logo_scale ?? 100,
            kind: "pwhl",
        };
    }
    function ncaa(school) {
        if (!school) return { name: "", abbr: "", primary_color: "#1E2233", text_color: "#FFFFFF", logo_url: "", logo_bg: "auto", kind: "ncaa" };
        const key = ncaaOverrideKey(school);
        const ov = key ? overridesMap[key] : null;
        return {
            id: key || school,
            name: resolveName(school, ov),
            abbr: ov?.abbreviation || "",
            primary_color: ov?.primary_color || "#1E2233",
            text_color: ov?.text_color || "#FFFFFF",
            logo_url: ov?.logo_url || "",
            logo_opacity: ov?.logo_opacity ?? 1,
            logo_bg: ov?.logo_bg || "auto",
            logo_scale: ov?.logo_scale ?? 100,
            kind: "ncaa",
        };
    }
    function custom(t) {
        return {
            id: t.id,
            name: t.name || t.id,
            abbr: t.abbreviation || (t.name || "").slice(0, 3).toUpperCase(),
            primary_color: t.primary_color || "#1E2233",
            text_color: t.text_color || "#FFFFFF",
            logo_url: t.logo_url || "",
            logo_opacity: 1,
            logo_bg: t.logo_bg || "auto",
            logo_scale: t.logo_scale ?? 100,
            kind: "custom",
        };
    }
    function league(name) {
        const idx = LEAGUE_TEAM_INDEX[name];
        if (!idx) return null;
        const key = `lg-${idx.leagueId}-${name}`;
        const ov = overridesMap[key];
        return {
            id: key,
            name: resolveName(name, ov),
            abbr: ov?.abbreviation || "",
            primary_color: ov?.primary_color || idx.color || "#1E2233",
            text_color: ov?.text_color || "#FFFFFF",
            logo_url: ov?.logo_url || "",
            logo_opacity: ov?.logo_opacity ?? 1,
            logo_bg: ov?.logo_bg || "auto",
            logo_scale: ov?.logo_scale ?? 100,
            kind: "league",
        };
    }
    function any(ref) {
        if (!ref) return { name: "", abbr: "", primary_color: "#1E2233", text_color: "#FFFFFF", logo_url: "", logo_bg: "auto" };
        if (TEAM_INFO[ref]) return pwhl(ref);
        if (customMap[ref]) return custom(customMap[ref]);
        const lg = league(ref);
        if (lg) return lg;
        return ncaa(ref);
    }
    return { pwhl, ncaa, custom, league, any, displayName: (ref) => any(ref).name };
}

export function useTeamLookup() {
    const { data: overrides } = useQuery({
        queryKey: ["team-overrides"],
        queryFn: () => api.get("/team_overrides").then((r) => r.data),
        staleTime: 60 * 1000,
    });
    const { data: customTeams } = useQuery({
        queryKey: ["custom-teams"],
        queryFn: () => api.get("/custom_teams").then((r) => r.data),
        staleTime: 60 * 1000,
    });
    const overridesMap = overrides || {};
    const customMap = {};
    (customTeams || []).forEach((t) => { customMap[t.id] = t; });
    return { overrides: overridesMap, customTeams: customTeams || [], ...buildResolvers(overridesMap, customMap) };
}
